<template>
	<div class="session-control-sip">
		<ui-form
			:valid.sync="formValid"
			@submit="startSession(contact, pinCode, pinDelay)"
		>
			<ui-autocomplete
				ref="contactInput"
				v-model="contact"
				:options="contactUriList"
				:pattern="/^(?!(http|rtmp)s?:\/\/)/i"
				pattern-error="Invalid source address (see help page for details)"
				class="session-control-sip__contactSelect"
				label="Source address"
				placeholder="Source video address: SIP/H.323 (URI or IP)"
				@change="onContactChange"
				@enter="onContactEnter"
				@select="onContactSelect"
			/>
			<pin-input
				v-model="pinCode"
				:pin-delay.sync="pinDelay"
				class="session-control-sip__contactSelect__pinInput"
			/>
			<ui-button
				:disabled="contact.length === 0 || !formValid || isConnecting"
				:icon="`fas ${isConnecting ? 'fa-circle-notch fa-spin' : 'fa-circle'}`"
				:text="streamId !== null ? 'STREAM' : 'RECORD'"
				class="session-control-sip__streamRecordButton"
				large
				type="submit"
				raised
				secondary
			/>
		</ui-form>
		<div class="session-control-sip__favorites">
			<favorite-chip
				v-for="item in favoriteList"
				:key="item.uri"
				:disabled="isConnecting"
				:name="item.name"
				:tooltip="getFavoriteTooltip(item)"
				:uri="item.uri"
				clickable
				@click="startFavoriteSession($event)"
				@edit="editFavorite(item)"
			/>
		</div>
	</div>
</template>

<script>
import FavoriteChip from "session/ui/FavoriteChip";
import PinInput from "session/ui/PinInput";
import UiAutocomplete from "lib/components/ui/Autocomplete";
import UiButton from "lib/components/ui/Button";
import UiForm from "lib/components/ui/Form";
import alert from "lib/modules/Alert";
import http from "lib/modules/Http";

import find from "lodash/find";

const PIN_DELAY = 5;

export default {
	name: "SessionControlSip",
	components: {
		FavoriteChip,
		PinInput,
		UiAutocomplete,
		UiButton,
		UiForm
	},
	props: {
		contactList: {
			type: Array,
			required: true
		},
		pendingContacts: {
			type: Object,
			required: true
		},
		recorder: {
			type: Object,
			required: true
		},
		sessionList: {
			type: Array,
			required: true
		},
		streamId: {
			required: true,
			validator: (prop) => typeof prop === "string" || prop === null // custom validator since null is not a valid type
		}
	},
	data() {
		return {
			contact: "",
			formValid: false,
			isConnecting: false,
			pinCode: null,
			pinDelay: PIN_DELAY
		};
	},
	computed: {
		contactUriList() {
			return this.contactList.map((item) => item.uri);
		},
		favoriteList() {
			return this.contactList.filter((item) => item.favorite === 1);
		}
	},
	methods: {
		// favorites
		editFavorite(favorite) {
			$bus.$emit("_session/ui/FavoriteDialog.open", favorite);
		},
		getFavoriteTooltip(item) {
			let tooltip = item.name ? item.uri : "";

			if (item.dtmf !== null) {
				tooltip += tooltip.length > 0 ? " | " : "";
				tooltip += `PIN: ${item.dtmf} | Delay: ${item.dtmf_delay}s`;
			}

			return tooltip;
		},
		// contact
		onContactChange(uri) {
			if (uri.length === 0) {
				this.pinCode = null;
				this.pinDelay = PIN_DELAY;
			}
		},
		onContactEnter() {
			this.$refs.contactInput.close();
		},
		onContactSelect(uri) {
			const contact = find(this.contactList, ["uri", uri]);

			if (!contact)
				return;

			this.pinCode = contact.dtmf;
			this.pinDelay = contact.dtmf_delay ? contact.dtmf_delay : PIN_DELAY;
		},
		// session
		startFavoriteSession(uri) {
			const contact = find(this.contactList, ["uri", uri]);

			if (!contact)
				return;

			this.startSession(uri, contact.dtmf, contact.dtmf_delay);
		},
		startSession(contact, pinCode, pinDelay) {
			// check if stream is already in use
			if (this.streamId !== null && find(this.sessionList, { stream_id: this.streamId })) {
				alert.error("Stream is currently in use by another session");

				return;
			}

			const parameters = {
				uri: contact
			};

			if (pinCode !== null) {
				parameters.dtmf = pinCode;
				parameters.dtmf_delay = pinDelay;
			}

			if (this.streamId !== null)
				parameters.stream_id = this.streamId;

			this.isConnecting = true;

			http.post(`${location.protocol}//${this.recorder.server.hostname}/api/v2/recorder/${this.recorder.recorder_id}/session`, parameters)
			.then(() => {
				// the below object syntax may look weird, this is object spread operator used
				// together with shorthand condition, the result is that properties are conditionally
				// added instead of adding properties with undefined or null values
				this.$emit("updatePendingContacts", {
					...this.pendingContacts,
					[parameters.uri]: {
						...parameters.dtmf && { dtmf: parameters.dtmf },
						...parameters.dtmf && parameters.dtmf_delay && { dtmf_delay: parameters.dtmf_delay }
					}
				});

				$bus.$emit("_App.getSessionStatus");
			})
			.catch((error) => {
				alert.error("Unable to create session", error.response);
			})
			.finally(() => {
				this.isConnecting = false;
			});
		}
	}
};
</script>

<style lang="scss" scoped>
.session-control-sip {
	/deep/ form {
		display: grid;
		grid-gap: 0 16px;
		grid-template-columns: auto 260px 215px;
		.session-control-sip__contactSelect,
		.session-control-sip__contactSelect__pinInput {
			margin-top: 0;
		}
		.session-control-sip__streamRecordButton {
			margin: 0;
			transition: none; // Vue Material does transition animation when margin changes
		}
		@media (max-width: 983px) {
			grid-template-columns: auto 260px;
			.session-control-sip__streamRecordButton {
				grid-column: span 2;
				margin: 4px 0 16px 0;
			}
		}
		@media (max-width: 767px) {
			grid-template-columns: none;
			.session-control-sip__contactSelect {
				grid-column: auto;
			}
			.session-control-sip__streamRecordButton {
				grid-column: auto;
			}
		}
	}
	.session-control-sip__favorites {
		margin-top: -6px; // compensate for chips that has 8px top margin to have some margin when they wrap
	}
}

// IE11 specific, using flex due to poor grid support
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
	.session-control-sip {
		width: 100%;
		/deep/ form {
			display: flex;
			flex-flow: row wrap;
			.session-control-sip__contactSelect {
				flex-basis: 100%;
				flex-grow: 1;
				@media (min-width: 768px) {
					flex-basis: 0;
				}
			}
			.session-control-sip__contactSelect__pinInput {
				flex-basis: 100%;
				@media (min-width: 768px) {
					flex-basis: 260px;
					margin-left: 16px;
				}
			}
			.session-control-sip__streamRecordButton {
				flex-basis: 100%;
				@media (min-width: 984px) {
					margin-top: 4px;
					margin-left: 16px;
					flex-basis: 215px;
				}
			}
			.session-control-sip__favorites {
				flex-basis: 100%;
			}
		}
	}
}
</style>
